import { useContext, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { DelegateDocumentRequest, RedelegateDocumentRequest } from 'api';
import { MAX_DELEGATION_DEPTH } from 'common/constants/delegation';
import { RequestedFileExtended } from 'common/types/files';
import { SecurePortalParams } from 'common/types/reactRouter';
import { TaskContext } from 'context/TaskContext';
import { TaskContextType } from 'context/TaskContext/types';
import { Question } from 'dashboard409a/pages/Information/types';
import { getNotificationLabels, isValidEmail } from 'dashboard409a/pages/Information/util/delegationValidation';
import { useDocuments } from 'services/hooks';
import { DelegateModalProps } from './DelegateModal.props';

const DelegateModal = ({ open, onClose, onSuccess, redelegateTarget }: DelegateModalProps) => {
  const { requestId, hmacToken } = useParams<SecurePortalParams>();
  const { delegateDocumentRequest, redelegateDocumentRequest, isLoading } = useDocuments();
  const { requestedFiles, requestedQuestions, task } = useContext(TaskContext) as TaskContextType;

  const [formData, setFormData] = useState({
    delegatee_name: '',
    delegatee_email: '',
    notification_preference: DelegateDocumentRequest.notification_preference.EACH_UPLOAD,
  });
  const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([]);
  const [delegateAllFiles, setDelegateAllFiles] = useState(true);
  const [delegateAllQuestions, setDelegateAllQuestions] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isRedelegate = !!redelegateTarget;
  const title = isRedelegate ? 'Reassign Request' : 'Delegate Request';
  const submitLabel = isRedelegate ? 'Reassign' : 'Send Delegation';

  // Filter out files that are already uploaded for delegation
  const delegatableFiles = requestedFiles?.filter((f: RequestedFileExtended) => !f.is_uploaded) || [];

  // Filter out questions that are already answered for delegation
  const delegatableQuestions = requestedQuestions?.filter((q: Question) => !q.answered) || [];

  const hasFiles = delegatableFiles.length > 0;
  const hasQuestions = delegatableQuestions.length > 0;
  const hasBoth = hasFiles && hasQuestions;

  // Check delegation depth
  const delegationDepth = task?.task?.delegation_depth ?? 0;
  const canDelegate = delegationDepth < MAX_DELEGATION_DEPTH;
  const isMaxDepthReached = !canDelegate;

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileToggle = (fileId: number) => {
    setSelectedFileIds(prev =>
      prev.includes(fileId) ? prev.filter(id => id !== fileId) : [...prev, fileId]
    );
  };

  const handleQuestionToggle = (questionId: number) => {
    setSelectedQuestionIds(prev =>
      prev.includes(questionId) ? prev.filter(id => id !== questionId) : [...prev, questionId]
    );
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.delegatee_name.trim()) {
      newErrors.delegatee_name = 'Name is required';
    }

    if (!formData.delegatee_email.trim()) {
      newErrors.delegatee_email = 'Email is required';
    } else if (!isValidEmail(formData.delegatee_email)) {
      newErrors.delegatee_email = 'Invalid email format';
    }

    // Validate file selection (only if not re-delegating and has files)
    if (!isRedelegate && hasFiles && !delegateAllFiles && selectedFileIds.length === 0) {
      newErrors.files = 'Please select at least one document to delegate';
    }

    // Validate question selection (only if not re-delegating and has questions)
    if (!isRedelegate && hasQuestions && !delegateAllQuestions && selectedQuestionIds.length === 0) {
      newErrors.questions = 'Please select at least one question to delegate';
    }

    // Must have at least something to delegate
    if (!isRedelegate) {
      const willDelegateFiles = hasFiles && (delegateAllFiles || selectedFileIds.length > 0);
      const willDelegateQuestions = hasQuestions && (delegateAllQuestions || selectedQuestionIds.length > 0);

      if (!willDelegateFiles && !willDelegateQuestions) {
        newErrors.general = 'Nothing to delegate';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || !requestId || !hmacToken) {
      return;
    }

    const basePayload = {
      delegatee_name: formData.delegatee_name.trim(),
      delegatee_email: formData.delegatee_email.trim(),
      notification_preference: formData.notification_preference,
      // Add file IDs if partial file delegation
      requested_file_ids: !delegateAllFiles && selectedFileIds.length > 0 ? selectedFileIds : undefined,
      // Add question IDs if partial question delegation
      question_ids: !delegateAllQuestions && selectedQuestionIds.length > 0 ? selectedQuestionIds : undefined,
    };

    try {
      if (isRedelegate && redelegateTarget) {
        const redelegatePayload: RedelegateDocumentRequest = {
          delegation_id: redelegateTarget.id,
          delegatee_name: basePayload.delegatee_name,
          delegatee_email: basePayload.delegatee_email,
          notification_preference: basePayload.notification_preference,
        };
        await redelegateDocumentRequest(requestId, hmacToken, redelegatePayload);
      } else {
        const delegatePayload: DelegateDocumentRequest = basePayload;
        await delegateDocumentRequest(requestId, hmacToken, delegatePayload);
      }

      resetForm();
      onSuccess();
    } catch (error) {
      // Error notification is handled by the hook
    }
  };

  const resetForm = () => {
    setFormData({
      delegatee_name: '',
      delegatee_email: '',
      notification_preference: DelegateDocumentRequest.notification_preference.EACH_UPLOAD,
    });
    setSelectedFileIds([]);
    setSelectedQuestionIds([]);
    setDelegateAllFiles(true);
    setDelegateAllQuestions(true);
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Determine notification label based on what's being delegated
  const notificationLabels = useMemo(
    () => getNotificationLabels(hasFiles, hasQuestions),
    [hasFiles, hasQuestions]
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {isMaxDepthReached && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Maximum delegation depth of {MAX_DELEGATION_DEPTH} has been reached. This request cannot be delegated further.
          </Alert>
        )}

        {isRedelegate && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="body2" color="warning.dark">
              This will reassign the request from{' '}
              <strong>{redelegateTarget?.delegatee_name}</strong> to a new person.
              The previous delegate will lose access.
            </Typography>
          </Box>
        )}

        {errors.general && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography variant="body2" color="error.dark">
              {errors.general}
            </Typography>
          </Box>
        )}

        <TextField
          label="Recipient Name"
          fullWidth
          required
          value={formData.delegatee_name}
          onChange={handleChange('delegatee_name')}
          error={!!errors.delegatee_name}
          helperText={errors.delegatee_name}
          margin="normal"
          autoFocus
        />

        <TextField
          label="Recipient Email"
          fullWidth
          required
          type="email"
          value={formData.delegatee_email}
          onChange={handleChange('delegatee_email')}
          error={!!errors.delegatee_email}
          helperText={errors.delegatee_email}
          margin="normal"
        />

        {/* File Selection for Partial Delegation */}
        {!isRedelegate && hasFiles && (
          <Box sx={{ mt: 2 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Documents to delegate</FormLabel>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={delegateAllFiles}
                    onChange={e => setDelegateAllFiles(e.target.checked)}
                  />
                }
                label={`Delegate all documents (${delegatableFiles.length})`}
              />
            </FormControl>

            {!delegateAllFiles && (
              <Box sx={{ ml: 2, mt: 1, maxHeight: 150, overflow: 'auto' }}>
                {delegatableFiles.map((file: RequestedFileExtended) => (
                  <FormControlLabel
                    key={file.id}
                    control={
                      <Checkbox
                        checked={selectedFileIds.includes(file.id as number)}
                        onChange={() => handleFileToggle(file.id as number)}
                        size="small"
                      />
                    }
                    label={file.name}
                    sx={{ display: 'block' }}
                  />
                ))}
                {errors.files && (
                  <Typography variant="caption" color="error">
                    {errors.files}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Question Selection for Partial Delegation */}
        {!isRedelegate && hasQuestions && (
          <Box sx={{ mt: 2 }}>
            {hasFiles && <Divider sx={{ my: 2 }} />}
            <FormControl component="fieldset">
              <FormLabel component="legend">Questions to delegate</FormLabel>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={delegateAllQuestions}
                    onChange={e => setDelegateAllQuestions(e.target.checked)}
                  />
                }
                label={`Delegate all questions (${delegatableQuestions.length})`}
              />
            </FormControl>

            {!delegateAllQuestions && (
              <Box sx={{ ml: 2, mt: 1, maxHeight: 150, overflow: 'auto' }}>
                {delegatableQuestions.map((question: Question) => (
                  <FormControlLabel
                    key={question.id}
                    control={
                      <Checkbox
                        checked={selectedQuestionIds.includes(question.id as number)}
                        onChange={() => handleQuestionToggle(question.id as number)}
                        size="small"
                      />
                    }
                    label={question.name}
                    sx={{ display: 'block' }}
                  />
                ))}
                {errors.questions && (
                  <Typography variant="caption" color="error">
                    {errors.questions}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Notification Preferences */}
        <Box sx={{ mt: 2 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Notification preference</FormLabel>
            <RadioGroup
              value={formData.notification_preference}
              onChange={handleChange('notification_preference')}
            >
              <FormControlLabel
                value={DelegateDocumentRequest.notification_preference.EACH_UPLOAD}
                control={<Radio size="small" />}
                label={notificationLabels.each}
              />
              <FormControlLabel
                value={DelegateDocumentRequest.notification_preference.ALL_COMPLETE}
                control={<Radio size="small" />}
                label={notificationLabels.all}
              />
              <FormControlLabel
                value={DelegateDocumentRequest.notification_preference.NONE}
                control={<Radio size="small" />}
                label={notificationLabels.none}
              />
            </RadioGroup>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Notifications will be sent to you. The delegatee will only see the delegated items in their portal.
            </Typography>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isLoading || isMaxDepthReached}>
          {isLoading ? 'Sending...' : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DelegateModal;
